import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import GoogleOAuthButton from "./GoogleOAuthButton";
import { Loader2 } from "lucide-react";
import { useTurnstile } from "@/hooks/useTurnstile";
import { userApiClient } from "@/lib/userApiClient";
import PasswordStrengthSideIndicator from "./PasswordStrengthSideIndicator";
import { AgreementCheckbox } from "@/components/consent";
import { useLatestAgreements } from "@/hooks/useLatestAgreements";
import { useRegistrationFlow } from "@/hooks/useRegistrationFlow";
import RegistrationBlockingModal from "./RegistrationBlockingModal";

/**
 * InternationalRegisterPanel 组件属性
 */
export interface InternationalRegisterPanelProps {
  /** 邀请 token（可选） */
  inviteToken?: string | null;
  /** 注册成功后重定向路径 */
  redirectTo?: string;
}

/**
 * 国际注册面板
 * 支持：邮箱+密码注册、Google OAuth、Apple 注册
 * 支持邀请链接注册（invite_token）
 */
export default function InternationalRegisterPanel({
  inviteToken,
  redirectTo = "/",
}: InternationalRegisterPanelProps) {
  const { t } = useTranslation(["login", "consent"]);
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [agreementsChecked, setAgreementsChecked] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  // 获取最新协议
  const { agreements } = useLatestAgreements({ regionCode: "global" });

  // 区域感知的注册流程逻辑
  const { handleRegister, modalProps } = useRegistrationFlow({
    agreementsChecked,
    setAgreementsChecked,
    agreements,
  });

  // 使用 useTurnstile Hook
  const {
    containerRef: turnstileRef,
    token: turnstileToken,
    isLoading: turnstileLoading,
    isAvailable: turnstileAvailable,
    reset: resetTurnstile,
  } = useTurnstile({
    theme: "auto",
    size: "flexible",
    onError: () => {
      console.warn("Turnstile verification failed");
    },
    onExpire: () => {
      console.warn("Turnstile token expired");
    },
  });

  // 注册表单提交的实际逻辑
  const doSubmit = async () => {
    const email = emailRef.current?.value.trim();

    setIsLoading(true);

    try {
      await userApiClient.post(
        "/auth/register",
        {
          email,
          password,
          turnstile_token: turnstileToken,
          country_region: "global",
          signup_channel: "email",
          // 如果有邀请 token，添加到请求体
          ...(inviteToken && { invite_token: inviteToken }),
          // 添加协议签署信息
          agreement_ids: agreements
            .filter((a) => a.type === "tos" || a.type === "privacy")
            .map((a) => a.id),
        },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        },
      );

      // 国际用户需要验证邮箱，但如果是从特定页面来的，保存重定向信息
      const verifyUrl = `/verify-email?email=${encodeURIComponent(email!)}&registered=true`;
      if (redirectTo !== "/") {
        window.location.href = `${verifyUrl}&redirect=${encodeURIComponent(redirectTo)}`;
      } else {
        window.location.href = verifyUrl;
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Register failed:", error);
      toast.error(
        error instanceof Error ? error.message : t("common.networkError"),
      );

      resetTurnstile();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = emailRef.current?.value.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("common.invalidEmail"));
      return;
    }

    if (!password || password.length < 6) {
      toast.error(t("common.invalidPassword"));
      return;
    }

    if (turnstileAvailable && !turnstileToken) {
      toast.error(t("common.completeCaptcha", "请完成人机验证"));
      return;
    }

    // 使用区域感知的注册流程逻辑
    // 非 CN 区域在点击注册时触发协议确认
    handleRegister(doSubmit);
  };

  const handleAppleRegister = () => {
    toast.info(
      t("international.appleComingSoon", "Apple Sign-Up coming soon..."),
    );
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-label={t("international.registerButton", "注册")}
        aria-busy={isLoading}
      >
        <div>
          <Input
            id="reg-email"
            type="email"
            placeholder={t("international.emailPlaceholder")}
            ref={emailRef}
            autoComplete="email"
            aria-required="true"
            aria-label={t("international.emailLabel")}
            className="bg-background dark:bg-background h-10"
          />
        </div>

        <div className="relative">
          <Input
            id="reg-password"
            type="password"
            placeholder={t("international.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            autoComplete="new-password"
            aria-required="true"
            aria-label={t("international.passwordLabel")}
            className="bg-background dark:bg-background h-10"
          />
          <PasswordStrengthSideIndicator
            password={password}
            visible={passwordFocused}
          />
        </div>

        {/* Turnstile - 使用 Hook */}
        {turnstileAvailable && (
          <div
            className="w-full"
            aria-label={t("common.captchaVerification", "人机验证")}
          >
            <div ref={turnstileRef} />
          </div>
        )}

        {/* 协议勾选 */}
        {agreements.length > 0 && (
          <div className="space-y-3">
            <AgreementCheckbox
              agreements={agreements}
              checked={agreementsChecked}
              onCheckedChange={setAgreementsChecked}
            />
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={
            isLoading ||
            turnstileLoading ||
            (turnstileAvailable && !turnstileToken)
          }
          aria-busy={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading
            ? t("international.registering", "注册中...")
            : t("international.registerButton", "注册")}
        </Button>

        {/* 区域感知的注册阻断弹窗 */}
        <RegistrationBlockingModal {...modalProps} />
      </form>

      {/* 第三方注册 */}
      <div className="space-y-3">
        <GoogleOAuthButton
          region="global"
          variant="outline"
          inviteToken={inviteToken}
          redirectTo={redirectTo}
          onLoginStart={() => setIsLoading(true)}
          onError={() => setIsLoading(false)}
        />

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="w-full"
          onClick={handleAppleRegister}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          {t("international.appleLogin")}
        </Button>
      </div>
    </div>
  );
}
